import {
  groupByFestivalDay,
  localized,
  zonedInputToInstant,
  type Locale,
} from "@festipal/shared";
import { useCallback, useEffect, useState } from "react";

import { festivals } from "./api";
import { LineupImport } from "./LineupImport";
import { LineupPaste } from "./LineupPaste";

/**
 * Édition de la programmation.
 *
 * La grille est groupée par journée festivalière, comme dans l'application : un
 * organisateur doit voir sa soirée telle que ses festivaliers la verront, sans
 * qu'un concert de fin de nuit bascule au lendemain sous ses yeux.
 */

type Stage = { id: string; position: number; name: Record<string, string> };
type Artist = { id: string; name: string };
type Performance = {
  id: string;
  stageId: string;
  artistId: string;
  startsAt: string;
  endsAt: string;
  festivalDay: string;
  status: string;
};

type BundleShape = {
  festival: { defaultLocale: Locale; timezone: string };
  stages: Stage[];
  artists: Artist[];
  performances: Performance[];
};

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: "prévu",
  DELAYED: "retardé",
  CANCELLED: "annulé",
  REPLACED: "remplacé",
};

export function LineupEditor({ slug }: { slug: string }) {
  const [bundle, setBundle] = useState<BundleShape | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [stageName, setStageName] = useState("");
  const [draft, setDraft] = useState({
    stageId: "",
    artistName: "",
    startsAt: "",
    endsAt: "",
  });

  const reload = useCallback(async () => {
    const data = (await festivals.bundle(slug)) as BundleShape;
    setBundle(data);
    setDraft((current) => ({
      ...current,
      stageId: current.stageId || (data.stages[0]?.id ?? ""),
    }));
  }, [slug]);

  useEffect(() => {
    void reload().catch(() => setError("Lecture impossible."));
  }, [reload]);

  if (!bundle) return <p className="muted">Chargement…</p>;

  const locale = bundle.festival.defaultLocale;
  const stageName_ = (id: string) => {
    const stage = bundle.stages.find((s) => s.id === id);
    return stage ? localized(stage.name, locale, locale) : "—";
  };
  const artistName = (id: string) =>
    bundle.artists.find((a) => a.id === id)?.name ?? "—";

  /** Heure locale du site : c'est celle que l'organisateur a en tête. */
  const timeOf = (iso: string) =>
    new Date(iso).toLocaleTimeString("fr-FR", {
      timeZone: bundle.festival.timezone,
      hour: "2-digit",
      minute: "2-digit",
    });

  const days = groupByFestivalDay(bundle.performances);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setError(null);
    try {
      await action();
      await reload();
    } catch {
      setError(failure);
    }
  };

  return (
    <>
      {error ? <p className="error">{error}</p> : null}

      <section className="card">
        <h2>Scènes</h2>

        <div className="table-scroll">
          <table>
            <tbody>
              {bundle.stages.map((stage) => (
                <tr key={stage.id}>
                  <td>{localized(stage.name, locale, locale)}</td>
                  <td style={{ width: 120, textAlign: "right" }}>
                    <button
                      className="danger quiet"
                      onClick={() =>
                        void run(
                          () => festivals.deleteStage(slug, stage.id),
                          "Scène non supprimée : elle a encore des concerts.",
                        )
                      }
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="row">
          <label style={{ flex: 1, minWidth: 220 }}>
            Nouvelle scène
            <input
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              placeholder="Ex. : Chapiteau"
            />
          </label>
          <button
            className="primary"
            disabled={!stageName.trim()}
            onClick={() =>
              void run(async () => {
                await festivals.createStage(slug, {
                  translations: { [locale]: { name: stageName.trim() } },
                });
                setStageName("");
              }, "Création impossible.")
            }
          >
            Ajouter
          </button>
        </div>
      </section>

      <LineupImport slug={slug} onImported={() => void reload()} />
      <LineupPaste slug={slug} onImported={() => void reload()} />

      <section className="card">
        <h2>Ajouter un concert</h2>
        <div className="grid-2">
          <label>
            Scène
            <select
              value={draft.stageId}
              onChange={(e) => setDraft({ ...draft, stageId: e.target.value })}
            >
              {bundle.stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {localized(stage.name, locale, locale)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Artiste
            <input
              value={draft.artistName}
              onChange={(e) => setDraft({ ...draft, artistName: e.target.value })}
            />
          </label>
          <label>
            Début
            <input
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
            />
          </label>
          <label>
            Fin
            <input
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
            />
          </label>
        </div>

        <div className="row">
          <button
            className="primary"
            disabled={
              !draft.stageId ||
              !draft.artistName.trim() ||
              !draft.startsAt ||
              !draft.endsAt
            }
            onClick={() =>
              void run(async () => {
                await festivals.createPerformance(slug, {
                  stageId: draft.stageId,
                  artistName: draft.artistName.trim(),
                  // Typed in the site's time, whatever the organiser's computer
                  // is set to.
                  startsAt: zonedInputToInstant(
                    draft.startsAt,
                    bundle.festival.timezone,
                  )!.toISOString(),
                  endsAt: zonedInputToInstant(
                    draft.endsAt,
                    bundle.festival.timezone,
                  )!.toISOString(),
                });
                setDraft({ ...draft, artistName: "", startsAt: "", endsAt: "" });
              }, "Création impossible.")
            }
          >
            Ajouter le concert
          </button>
          <span className="muted">
            Les heures se saisissent dans le fuseau de votre navigateur et sont
            affichées dans celui du site.
          </span>
        </div>
      </section>

      {days.map((day) => (
        <section className="card" key={day.day}>
          <h2>
            {new Date(`${day.day}T12:00:00Z`).toLocaleDateString("fr-FR", {
              timeZone: "UTC",
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </h2>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Heure</th>
                  <th>Artiste</th>
                  <th>Scène</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {day.performances.map((performance) => (
                  <tr key={performance.id}>
                    <td>{timeOf(performance.startsAt)}</td>
                    <td>{artistName(performance.artistId)}</td>
                    <td>{stageName_(performance.stageId)}</td>
                    <td>
                      <select
                        value={performance.status}
                        onChange={(e) =>
                          void run(
                            () =>
                              festivals.updatePerformance(slug, performance.id, {
                                status: e.target.value,
                              }),
                            "Modification impossible.",
                          )
                        }
                      >
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="danger quiet"
                        onClick={() =>
                          void run(
                            () => festivals.deletePerformance(slug, performance.id),
                            "Suppression impossible.",
                          )
                        }
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {days.length === 0 ? (
        <section className="card">
          <p className="muted">Aucun concert pour l'instant.</p>
        </section>
      ) : null}
    </>
  );
}
